/**
 * TrialDoorModal — the 14-day trial, explained on tap (public, no account).
 *
 * Oct 2026 (Randy, fan-first pass). The landing page's trial doorway is
 * deliberately a small button with no sales copy around it: a visitor who is
 * curious taps it and reads the terms here, and a visitor who already knows
 * what they want is never made to walk past a trial pitch to find the door
 * they came for.
 *
 * Every number is read from /public/pricing. The in-app TrialStartModal is the
 * authenticated twin of this; this one only gets them to the signup form with
 * their intent intact.
 */
import { Link } from "react-router-dom";
import { Sparkle, CheckCircle, X, ArrowRight } from "@phosphor-icons/react";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";

export default function TrialDoorModal({ open, onClose }) {
  const pricing = usePublicPricing();
  if (!open) return null;
  const fee = fmt$(pricing.creator_monthly_fee_cents ?? pricing.platform_fee_cents);

  const lines = [
    <>Every Pro tool unlocked from minute one — not a feature-limited demo.</>,
    <><strong className="text-[#FFC24A]">{fee}/month begins on day 15.</strong> A card is required to
      start, and we email you three days before anything is charged.</>,
    <>Cancel in one click any time before day 15 and you are charged nothing — you keep the trial
      through its last day.</>,
    <>Pro tools you want to keep are charged separately, once, and only after you confirm each one.</>,
    <>AI answers are funded separately — your trial opens with a small credit at our cost so you can
      watch it work before funding anything.</>,
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="trial-door-modal"
    >
      <div className="absolute inset-0 bg-[#05080C]/70 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-lg rounded-2xl border border-[#FFC24A]/25 bg-[#0A0D0F]/95 text-[#F5F1E8] shadow-[0_30px_90px_-30px_rgba(0,0,0,0.9)] max-h-[88dvh] flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 p-6 pb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFC24A] flex items-center gap-2">
              <Sparkle size={12} weight="duotone" /> 14 days of everything
            </div>
            <h3 className="font-serif-display text-2xl mt-2 leading-tight">
              Build now, <em className="text-[#FFC24A] not-italic">Decide later</em>
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="trial-door-close"
            className="text-[#E7E2D6]/60 hover:text-[#FFC24A] p-1 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <ul className="px-6 space-y-2.5 overflow-y-auto flex-1">
          {lines.map((line, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-[#E7E2D6]/90">
              <CheckCircle size={15} weight="duotone" className="text-[#FFC24A] mt-[2px] shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <div className="p-6 pt-4 flex flex-col sm:flex-row gap-2.5">
          <Link
            to="/signup?as=trial"
            data-testid="trial-door-signup"
            className="flex-1 px-5 py-3 rounded-md bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A] transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            Create my account <ArrowRight size={15} />
          </Link>
          <button
            onClick={onClose}
            data-testid="trial-door-dismiss"
            className="px-5 py-3 rounded-md border border-[#F5F1E8]/20 text-[#E7E2D6]/80 hover:text-[#FFC24A] hover:border-[#FFC24A]/40 transition-colors text-sm"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
